import { useBrand } from '../InstanceName';

/**
 * The orchd mark: a conductor and the three parts it keeps in order.
 *
 * One component rather than one copy per surface. It was two copies —
 * the sidebar and the login card each carried the same inline `>_`
 * chevron — and when the instance was renamed both were missed, because
 * a sweep looking for the string "argv" cannot see a mark that never
 * spelled it. The favicon is necessarily a third copy (a static file the
 * browser fetches, not a React tree), so the geometry is duplicated
 * exactly once and `public/favicon.svg` says so.
 *
 * The plate is not drawn here. Every caller sits the mark on a CSS
 * `--accent-gradient` tile, so this renders the white glyph only.
 */
type Props = {
  /**
   * Rendered size in px. Callers sit this on a tile roughly 1.25x
   * its size — 26 on the sidebar's 32px tile, 32 on the login
   * card's 40px. Chosen by rendering, not by eye: the mark spans
   * about 64% of its viewBox, so a glyph sized to the tile the way
   * the old two-stroke chevron was leaves it visibly adrift.
   */
  size?: number;
};

export function BrandMark({ size = 20 }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
    >
      {/*
        0.42, where favicon.svg uses 0.24. Not an inconsistency: the
        favicon had to survive a 16px browser tab on a background it
        cannot know, and was tuned to fade to four clean dots there. This
        renders at a known size on a solid tile, where a visible ring is
        what makes the mark read as an orbit rather than as three specks.
      */}
      <circle
        cx="32"
        cy="32"
        r="16"
        stroke="#ffffff"
        strokeOpacity="0.42"
        strokeWidth="2.4"
        fill="none"
      />
      <circle cx="32" cy="16" r="4.6" fill="#ffffff" />
      <circle cx="45.86" cy="40" r="4.6" fill="#ffffff" />
      <circle cx="18.14" cy="40" r="4.6" fill="#ffffff" />
      <circle cx="32" cy="32" r="7.2" fill="#ffffff" />
    </svg>
  );
}

/**
 * The predecessor's mark: a stroked "W" on a dark tile.
 *
 * Its geometry and colours unchanged from wingd's own sidebar, login card
 * and favicon — the look `web.brand.mark: "wing"` asks for. Unlike the
 * conductor it carries its own plate, because the plate is part of it: a
 * dark tile with a hairline border, the accent in the stroke rather than
 * the fill.
 */
function WingMark({ size, accent, background }: { size: number; accent: string; background: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect width="32" height="32" rx="7" fill={background} stroke="#252836" strokeWidth="0.5" />
      <path
        d="M6 9 L11 22 L16 14 L21 22 L26 9"
        stroke={accent}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * The brand's tile, whichever mark it names.
 *
 * `className` sizes and places the tile; for the conductor it is also the
 * gradient plate the glyph sits on, which is how both callers drew it
 * before the panel was brandable. `glyph` is the conductor glyph's size on
 * that plate — see `BrandMark`'s own note on why it is not the tile's.
 */
export function BrandTile({
  className,
  tile,
  glyph,
}: {
  className: string;
  tile: number;
  glyph: number;
}) {
  const brand = useBrand();
  if (brand.mark === 'wing') {
    return (
      <div className={`${className} brand-tile--wing`}>
        <WingMark size={tile} accent={brand.accent} background={brand.background} />
      </div>
    );
  }
  return (
    <div className={className}>
      <BrandMark size={glyph} />
    </div>
  );
}

/**
 * The instance's name as the brand spells it.
 *
 * `web.brand.wordmark` is `[text, tone]` segments — wingd's was "wing" in
 * the primary text colour, "d" in the accent and ".sh" dimmed. Rendered as
 * text in three fixed tones, never as markup. With no wordmark configured
 * it is the instance name, plainly, which is what every surface showed
 * before.
 */
export function Wordmark({ className, name }: { className?: string; name: string }) {
  const brand = useBrand();
  if (brand.wordmark.length === 0) {
    return <span className={className}>{name}</span>;
  }
  return (
    <span className={className} aria-label={brand.wordmark.map(([t]) => t).join('')}>
      {brand.wordmark.map(([text, tone], i) => (
        <span key={i} className={`wordmark--${tone}`} aria-hidden="true">
          {text}
        </span>
      ))}
    </span>
  );
}
